import React, { useState } from 'react';
import {
    Container,
    Grid,
    GridItem,
    Box,
    Heading,
    Text,
    Image,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
    List,
    Link,
    ListItem,
    ListIcon,
    IconButton,
    VStack,
    HStack,
} from '@chakra-ui/react';
import {
    FiCode,
    FiSmartphone,
    FiGlobe,
    FiSearch,
    FiPenTool,
    FiShare2,
    FiDownload,
    FiArrowRight,
} from 'react-icons/fi';
import { FaFacebookF, FaYoutube, FaInstagram, FaTwitter, FaLinkedin } from 'react-icons/fa';
import { FaFeatherPointed, FaKey } from "react-icons/fa6";
import { TbSquareRoundedArrowRightFilled } from "react-icons/tb";
import '@fontsource/quicksand';
import websiteImage from '../../assets/WebDevelopment.webp';
import mobileImage from '../../assets/MobileAppDevelopment.webp';
import arvrImage from '../../assets/ARVR Development.webp';
import seoImage from '../../assets/SEO.webp';
import GraphicDesignImg from '../../assets/GraphicDesign.webp';
import socialImage from '../../assets/SocialMediaMarketing.webp';

const servicesList = [
    { name: 'Website Development', icon: FiCode },
    { name: 'Mobile App Development', icon: FiSmartphone },
    { name: 'AR/VR Development', icon: FiGlobe },
    { name: 'SEO & Google Ads', icon: FiSearch },
    { name: 'Logo & Graphic Design', icon: FiPenTool },
    { name: 'Social Media Marketing', icon: FiShare2 },
];

const downloadsList = [
    { name: 'Service Report', icon: FiDownload },
    { name: 'All Services', icon: FiDownload },
];

const socialLinks = [
    { name: 'Facebook', icon: FaFacebookF, url: 'https://www.facebook.com/share/16AVRur1Gm/?mibextid=wwXIfr' },
    { name: 'Instagram', icon: FaInstagram, url: 'https://www.instagram.com/dremotechsolutions' },
    { name: 'Linkedin', icon: FaLinkedin, url: 'https://www.linkedin.com/company/dremotech' },
];

const servicesData = {
    'Website Development': {
        title: 'Website Development',
        featuredImage: websiteImage,
        description1: `
            Our Website Development services craft dynamic, responsive, and user-focused websites tailored to elevate your business. Whether you need an e-commerce platform, a corporate site, or a custom solution, we deliver engaging digital experiences that captivate your audience. Our team ensures your website is lightning-fast, highly secure, and optimized for search engines to strengthen your online presence.
        `,
        description2: `
            We specialize in creating bespoke web solutions that align with your brand’s goals, offering seamless functionality and scalability. From content management to third-party integrations, our streamlined development process guarantees timely delivery and a robust digital footprint that drives growth.
        `,
        images: [
            '/assets/images/services/web-development-1.jpg',
            '/assets/images/services/web-development-2.jpg',
        ],
        infoItems: [
            {
                number: '01',
                title: 'Responsive Design',
                text: 'Websites that adapt flawlessly across desktops, tablets, and mobile devices.',
            },
            {
                number: '02',
                title: 'Performance Optimization',
                text: 'Fast-loading pages designed to enhance user experience and retention.',
            },
            {
                number: '03',
                title: 'Secure Architecture',
                text: 'Advanced security measures to safeguard user data and transactions.',
            },
            {
                number: '04',
                title: 'Scalable Solutions',
                text: 'Flexible websites that evolve with your business demands.',
            },
        ],
        faqs: [
            {
                question: 'How long does it take to develop a website?',
                answer: 'A standard website typically takes 4-8 weeks, with custom projects varying based on complexity. We provide a detailed timeline during our consultation.',
            },
            {
                question: 'Do you offer website maintenance?',
                answer: 'Yes, we provide comprehensive maintenance services, including regular updates, backups, and performance enhancements.',
            },
            {
                question: 'Can you integrate third-party tools?',
                answer: 'Absolutely, we seamlessly integrate tools like payment gateways, CRMs, and analytics to enhance your website’s functionality.',
            },
            {
                question: 'What makes your websites stand out?',
                answer: 'Our focus on user-centric design, speed, security, and scalability ensures your website delivers exceptional value and impact.',
            },
        ],
    },
    'Mobile App Development': {
        title: 'Mobile App Development',
        featuredImage: mobileImage,
        description1: `
            Our Mobile App Development services transform your ideas into powerful, user-friendly applications for smartphones and tablets. We create apps that engage users and drive business growth, whether for consumer markets or enterprise needs. Our solutions prioritize intuitive interfaces, high performance, and seamless functionality to deliver outstanding mobile experiences.
        `,
        description2: `
            From concept to launch, our comprehensive development process covers design, development, testing, and deployment. We build scalable, secure apps with robust integrations, ensuring they meet your business objectives and delight users across devices.
        `,
        images: [
            '/assets/images/services/mobile-app-development-1.jpg',
            '/assets/images/services/mobile-app-development-2.jpg',
        ],
        infoItems: [
            {
                number: '01',
                title: 'Cross-Device Compatibility',
                text: 'Apps designed to work seamlessly on various mobile devices and operating systems.',
            },
            {
                number: '02',
                title: 'Custom UI/UX',
                text: 'Visually appealing and intuitive designs tailored to your brand.',
            },
            {
                number: '03',
                title: 'App Store Deployment',
                text: 'Hassle-free publishing to major app marketplaces.',
            },
            {
                number: '04',
                title: 'Ongoing Support',
                text: 'Continuous updates and maintenance to keep your app running smoothly.',
            },
        ],
        faqs: [
            {
                question: 'What types of apps do you develop?',
                answer: 'We create apps for various purposes, including consumer, enterprise, and utility applications, tailored to your needs.',
            },
            {
                question: 'How much does it cost to develop an app?',
                answer: 'Costs depend on features and complexity. We provide a detailed quote after reviewing your requirements.',
            },
            {
                question: 'Can you add features like push notifications?',
                answer: 'Yes, we incorporate engagement features like notifications and in-app messaging to enhance user interaction.',
            },
            {
                question: 'Do you offer app testing?',
                answer: 'We perform thorough testing for functionality, performance, and usability to ensure a high-quality app.',
            },
        ],
    },
    'AR/VR Development': {
        title: 'AR/VR Development',
        featuredImage: arvrImage,
        description1: `
            Our AR/VR Development services create immersive, interactive experiences that redefine engagement across industries like gaming, education, real estate, and training. We design augmented reality (AR) and virtual reality (VR) solutions that captivate users with stunning visuals and seamless interactions, bringing your ideas to life in virtual worlds.
        `,
        description2: `
            Our team crafts custom AR/VR applications with a focus on high-quality graphics and user engagement. From virtual tours to interactive simulations, we deliver solutions that perform flawlessly across devices, enhancing your brand’s innovation and impact.
        `,
        images: [
            '/assets/images/services/ar-vr-development-1.jpg',
            '/assets/images/services/ar-vr-development-2.jpg',
        ],
        infoItems: [
            {
                number: '01',
                title: 'Immersive Experiences',
                text: 'Captivating AR/VR apps with lifelike visuals and interactions.',
            },
            {
                number: '02',
                title: 'Device Compatibility',
                text: 'Solutions optimized for headsets, smartphones, and tablets.',
            },
            {
                number: '03',
                title: 'Custom Visuals',
                text: 'Tailored graphics and animations to suit your project’s needs.',
            },
            {
                number: '04',
                title: 'Industry Applications',
                text: 'Innovative solutions for education, healthcare, real estate, and more.',
            },
        ],
        faqs: [
            {
                question: 'What devices do your AR/VR solutions support?',
                answer: 'We develop for a range of devices, including VR headsets and AR-enabled smartphones or tablets.',
            },
            {
                question: 'How long does AR/VR development take?',
                answer: 'Projects typically take 8-16 weeks, depending on scope and complexity.',
            },
            {
                question: 'Can you create AR for marketing campaigns?',
                answer: 'Yes, we design interactive AR experiences, such as product visualizations and promotional filters.',
            },
            {
                question: 'Do you offer custom visual design for AR/VR?',
                answer: 'We create bespoke graphics, animations, and environments to enhance your AR/VR projects.',
            },
        ],
    },
    'SEO & Google Ads': {
        title: 'SEO & Google Ads',
        featuredImage: seoImage,
        description1: `
            Our SEO & Google Ads services amplify your online presence, driving traffic and conversions through strategic optimization and targeted advertising. We enhance your website’s visibility on search engines and create high-impact ad campaigns to connect with your ideal audience, ensuring maximum return on investment.
        `,
        description2: `
            Our holistic approach includes in-depth keyword strategies, on-page enhancements, and expertly managed pay-per-click (PPC) campaigns. We keep your brand competitive by adapting to the latest trends and delivering measurable results through data-driven insights.
        `,
        images: [
            '/assets/images/services/seo-google-ads-1.jpg',
            '/assets/images/services/seo-google-ads-2.jpg',
        ],
        infoItems: [
            {
                number: '01',
                title: 'Keyword Strategies',
                text: 'Carefully selected keywords to boost search engine rankings.',
            },
            {
                number: '02',
                title: 'Targeted PPC Campaigns',
                text: 'Ads designed to attract and convert your ideal customers.',
            },
            {
                number: '03',
                title: 'Performance Analytics',
                text: 'Comprehensive reports to monitor and optimize campaign success.',
            },
            {
                number: '04',
                title: 'Local Visibility',
                text: 'Enhanced exposure for location-based searches and audiences.',
            },
        ],
        faqs: [
            {
                question: 'How long does SEO take to show results?',
                answer: 'SEO typically yields results in 3-6 months, depending on competition and strategy.',
            },
            {
                question: 'Do you manage Google Ads budgets?',
                answer: 'Yes, we optimize budgets to maximize clicks and conversions within your constraints.',
            },
            {
                question: 'Can you improve my website’s ranking?',
                answer: 'Our proven strategies enhance search rankings and organic traffic effectively.',
            },
            {
                question: 'How do you measure campaign success?',
                answer: 'We provide detailed analytics to track traffic, conversions, and ROI, ensuring transparency.',
            },
        ],
    },
    'Logo & Graphic Design': {
        title: 'Logo & Graphic Design',
        featuredImage: GraphicDesignImg,
        description1: `
            Our Logo & Graphic Design services elevate your brand with visually compelling and memorable designs. We create logos, marketing materials, and digital assets that capture your brand’s essence and resonate with your audience, ensuring a strong and cohesive identity.
        `,
        description2: `
            From logo design to brochures, banners, and website visuals, our creative team delivers versatile graphics for print and digital platforms. We emphasize creativity, clarity, and consistency to enhance your brand’s presence and leave a lasting impression.
        `,
        images: [
            '/assets/images/services/logo-graphic-design-1.jpg',
            '/assets/images/services/logo-graphic-design-2.jpg',
        ],
        infoItems: [
            {
                number: '01',
                title: 'Custom Logos',
                text: 'Distinctive logos that embody your brand’s unique identity.',
            },
            {
                number: '02',
                title: 'Marketing Materials',
                text: 'Eye-catching brochures, flyers, and banners for effective campaigns.',
            },
            {
                number: '03',
                title: 'Digital Graphics',
                text: 'Engaging visuals for websites, social media, and advertisements.',
            },
            {
                number: '04',
                title: 'Brand Consistency',
                text: 'Unified design standards to strengthen your brand identity.',
            },
        ],
        faqs: [
            {
                question: 'How many logo concepts do you provide?',
                answer: 'We offer 3-5 initial concepts, with revisions to perfect your design.',
            },
            {
                question: 'What file formats do you deliver?',
                answer: 'We provide both vector (e.g., AI, EPS, SVG) and raster (e.g., PNG, JPG) formats.',
            },
            {
                question: 'Can you redesign an existing logo?',
                answer: 'Yes, we refresh existing logos to align with your evolving brand while preserving its core identity.',
            },
            {
                question: 'Do you create designs for social media?',
                answer: 'We design social media posts, cover images, and ad creatives to boost your online presence.',
            },
        ],
    },
    'Social Media Marketing': {
        title: 'Social Media Marketing',
        featuredImage: socialImage,
        description1: `
            Our Social Media Marketing services expand your brand’s reach through strategic campaigns on major platforms. We craft engaging content and targeted ads to connect with your audience, foster engagement, and drive conversions, building a loyal online community around your brand.
        `,
        description2: `
            From content creation to campaign management, we deliver a cohesive social media strategy that aligns with your business objectives. Our data-driven approach optimizes performance, ensuring your brand remains relevant and impactful in a dynamic digital landscape.
        `,
        images: [
            '/assets/images/services/social-media-marketing-1.jpg',
            '/assets/images/services/social-media-marketing-2.jpg',
        ],
        infoItems: [
            {
                number: '01',
                title: 'Engaging Content',
                text: 'Custom posts, videos, and stories designed for each platform.',
            },
            {
                number: '02',
                title: 'Targeted Advertising',
                text: 'Ads crafted to reach and convert your ideal audience.',
            },
            {
                number: '03',
                title: 'Performance Tracking',
                text: 'Detailed analytics to measure engagement and campaign success.',
            },
            {
                number: '04',
                title: 'Community Building',
                text: 'Fostering loyal followers through active engagement.',
            },
        ],
        faqs: [
            {
                question: 'Which platforms do you manage?',
                answer: 'We manage all major platforms, tailoring strategies to your business needs.',
            },
            {
                question: 'How often do you post content?',
                answer: 'We create customized schedules, typically posting 3-5 times per week per platform.',
            },
            {
                question: 'Can you run paid ad campaigns?',
                answer: 'Yes, we design and manage paid ads to enhance reach and drive results.',
            },
            {
                question: 'Do you provide social media analytics?',
                answer: 'We deliver comprehensive reports to track performance and refine strategies.',
            },
        ],
    },
};

function ServicesSingle() {
    const [selectedService, setSelectedService] = useState('Website Development');

    const handleServiceClick = (serviceName) => {
        setSelectedService(serviceName);
    };

    const currentService = servicesData[selectedService];

    return (
        <Box as="section" py={{ base: 8, md: 12 }} bg="customBlue">
            <Container maxW="container.xl">
                <Grid
                    templateColumns={{ base: '1fr', xl: '2fr 1fr' }}
                    gap={{ base: 6, md: 8 }}
                >
                    {/* Main Content */}
                    <GridItem>
                        <VStack spacing={{ base: 6, md: 8 }} align="start">
                            {/* Featured Image */}
                            {/* <Image
                                src={currentService.featuredImage}
                                alt={`${currentService.title} featured image`}
                                w="100%"
                                h={{ base: '200px', md: '400px' }}
                                objectFit="cover"
                                borderRadius="md"
                                loading="lazy"
                            /> */}

                            {/* Service Section 1 */}
                            <Box>
                                <Heading
                                    as="h2"
                                    fontSize={{ base: '2xl', md: '4xl' }}
                                    fontFamily="Pacifico, cursive"
                                    mb={5}
                                    data-aos="fade-up"
                                    data-aos-anchor-placement="top-bottom"
                                >
                                    {currentService.title}
                                </Heading>
                                <Text fontSize={{ base: 'md', md: 'lg' }} mb={4} data-aos="fade-up">
                                    {currentService.description1}
                                </Text>
                                <Text fontSize={{ base: 'md', md: 'lg' }} data-aos="fade-up">
                                    {currentService.description2}
                                </Text>
                            </Box>

                            <Image
                                src={currentService.featuredImage}
                                alt={`${currentService.title} featured image`}
                                w="100%"
                                h={{ base: '200px', md: '400px' }}
                                objectFit="cover"
                                borderRadius="md"
                                loading="lazy"
                                data-aos="zoom-out-up"
                                data-aos-duration="1000"
                            />

                            {/* Two-Column Images */}
                            {/* <Grid
                                templateColumns={{ base: '1fr', md: '1fr 1fr' }}
                                gap={{ base: 4, md: 6 }}
                            >
                                <Image
                                    src={currentService.featuredImage}
                                    alt={`${currentService.title} image 1`}
                                    w="100%"
                                    h={{ base: '150px', md: '250px' }}
                                    objectFit="cover"
                                    borderRadius="md"
                                    loading="lazy"
                                />
                                <Image
                                    src={currentService.images[1]}
                                    alt={`${currentService.title} image 2`}
                                    w="100%"
                                    h={{ base: '150px', md: '250px' }}
                                    objectFit="cover"
                                    borderRadius="md"
                                    loading="lazy"
                                />
                            </Grid> */}

                            {/* Service Section 2 */}
                            <Box>
                                <Heading
                                    as="h2"
                                    fontSize={{ base: '2xl', md: '3xl' }}
                                    fontFamily="Pacifico, cursive"
                                    mb={4}
                                >
                                    Why Choose Our {currentService.title}?
                                </Heading>
                                <Text fontSize={{ base: 'md', md: 'lg' }} mb={4}>
                                    {currentService.description1}
                                </Text>
                                <Text fontSize={{ base: 'md', md: 'lg' }}>
                                    {currentService.description2}
                                </Text>
                            </Box>

                            {/* Info Items */}
                            <Text fontSize={{ base: '2xl', md: '2xl' }} fontWeight="bold">Key Benefits </Text>
                            <Grid
                                templateColumns={{ base: '1fr', md: '1fr 1fr' }}
                                gap={{ base: 4, md: 6 }}
                                data-aos="fade-up"
                            >
                                {currentService.infoItems.map((item) => (
                                    <HStack key={item.number} align="start" spacing={4}>
                                        <Text
                                            fontSize={{ base: '2xl', md: '2xl' }}
                                            fontWeight="bold"
                                            color="customBlue.500"
                                        >
                                            {/* {item.number}. */}
                                            <FaFeatherPointed />
                                        </Text>
                                        <Box>
                                            <Heading as="h5" fontSize={{ base: 'lg', md: 'xl' }} mb={2}>
                                                {item.title}
                                            </Heading>
                                            <Text fontSize={{ base: 'sm', md: 'md' }}>{item.text}</Text>
                                        </Box>
                                    </HStack>
                                ))}
                            </Grid>

                            {/* FAQ Section */}
                            <Box w="100%" data-aos="flip-down">
                                <Heading
                                    as="h2"
                                    fontSize={{ base: '2xl', md: '3xl' }}
                                    fontFamily="Pacifico, cursive"
                                    mb={8}
                                    ml={5}

                                >
                                    Frequently Asked Questions
                                </Heading>
                                <Accordion allowToggle>
                                    {currentService.faqs.map((faq, index) => (
                                        <AccordionItem key={index} mt={3} border="none">
                                            <AccordionButton>
                                                <Box flex="1" textAlign="left">
                                                    <Heading as="h5" fontSize={{ base: 'md', md: 'lg' }}>
                                                        {faq.question}
                                                    </Heading>
                                                </Box>
                                                <AccordionIcon />
                                            </AccordionButton>
                                            <AccordionPanel pb={4} color={'gray.500'}>
                                                <Text fontSize={{ base: 'sm', md: 'md' }}>{faq.answer}</Text>
                                            </AccordionPanel>
                                        </AccordionItem>
                                    ))}
                                </Accordion>
                            </Box>
                        </VStack>
                    </GridItem>

                    {/* Sidebar */}
                    <GridItem data-aos="fade-down"
                        data-aos-easing="linear"
                        data-aos-duration="1500">
                        <VStack spacing={{ base: 6, md: 8 }} align="start">
                            {/* Services List */}
                            <Box w="100%">
                                <Heading
                                    as="h2"
                                    fontSize={{ base: 'xl', md: '2xl' }}
                                    fontFamily="Pacifico, cursive"
                                    mb={4}
                                    ml={10}
                                >
                                    List of Services
                                </Heading>
                                <List spacing={3}>
                                    {servicesList.map((service) => (
                                        <ListItem
                                            key={service.name}
                                            display="flex"
                                            alignItems="center"
                                            bg={selectedService === service.name ? 'customBlue.500' : 'transparent'}
                                            p={3}
                                            borderRadius="md"
                                            _hover={{ bg: 'customBlue.500', cursor: 'pointer' }}
                                            onClick={() => handleServiceClick(service.name)}
                                        >
                                            <ListIcon as={service.icon} color={selectedService === service.name ? 'customBlue.50' : 'customBlue.500'} mb={3} />
                                            <Text fontSize={{ base: 'md', md: 'lg' }} flex="1">
                                                {service.name}
                                            </Text>
                                            <TbSquareRoundedArrowRightFilled color={selectedService === service.name ? '#73FFCA' : 'white'} />
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>


                            {/* Social Links */}
                            <Box w="100%" ml={{ base: 0, md: 10 }} display={{ base: 'none', md: 'block' }}>
                                <Heading
                                    as="h2"
                                    fontSize={{ base: 'xl', md: '2xl' }}
                                    fontFamily="Pacifico, cursive"
                                    mb={4}
                                >
                                    Follow Us
                                </Heading>
                                <HStack spacing={4}>
                                    {socialLinks.map((social) => (
                                        <Link
                                            key={social.name}
                                            as="a"
                                            href={social.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label={social.name}
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            w="40px"
                                            h="40px"
                                            border="2px solid"
                                            borderColor="customBlue.500"
                                            borderRadius="full"
                                            color="customBlue.500"
                                            _hover={{ bg: 'customBlue.500', color: 'white' }}
                                        >
                                            <social.icon size="20px" />
                                        </Link>
                                    ))}
                                </HStack>
                            </Box>
                        </VStack>
                    </GridItem>
                </Grid>
            </Container>
        </Box>
    );
}

export default ServicesSingle;